import { IViolation } from '../base/annotation';
import { MappingDirective } from './mappingdirective';
import { shellStringResources } from '../../Resources/ShellStringResources';
import { AttributeMapping, MappingType } from './attributemapping';

export interface IViolationDetail {
    violationType: ViolationType;
    index: number;
    targetAttribute?: string;
    sourceAttribute?: string;
    mappingMethod?: string;
    targetTable?: string;
    sourceTable?: string;
}

export enum ViolationType {
    MappingMethod,
    SourceColumn,
    TargetColumn,
    ConnectionReference,
}

export function validateMappingDirectives(mappingDirective: MappingDirective): IViolation[] | undefined {
        const violations: IViolation[] = [];
        if (mappingDirective.attributeMappingInfo && mappingDirective.attributeMappingInfo.length > 0) {
            mappingDirective.attributeMappingInfo.forEach((attribute) => {
                if (!attribute.mappingType || attribute.mappingType === '') {
                    violations.push({
                        context: attribute,
                        message: () => `${mappingDirective.targetEntityReference.name} - ${shellStringResources.InvalidMappingType}.`
                    });
                }
                if (isSourceColumnInvalid(attribute, mappingDirective.attributeMappingInfo, mappingDirective.targetEntityReference.name)) {
                    violations.push({
                        context: attribute,
                        message: () => `${mappingDirective.targetEntityReference.name} - ${shellStringResources.InvalidSourceColumn}.`
                    });
                }
                if (!attribute.name || attribute.name === '') {
                    violations.push({
                        context: attribute,
                        message: () => `${mappingDirective.targetEntityReference.name} - ${shellStringResources.InvalidTargetColumn}.`
                    });
                }
            });
        }
        return violations;
}

export function validateMappingDirectivesInternal(mappingDirective: MappingDirective): IViolationDetail[] {
    const violations: IViolationDetail[] = [];
    let basicIdx = 0;
    let advIdx = 0;
    let advcdIdx = 0;
    const advMappingMethods = [MappingType.UNPIVOT.toString(), MappingType.LOOKUP.toString(), MappingType.SURROGATEKEY.toString() ];
    if (mappingDirective.attributeMappingInfo && mappingDirective.attributeMappingInfo.length > 0) {
        mappingDirective.attributeMappingInfo.forEach((attribute, index) => {
            let configIndex = index;
            if (advMappingMethods.includes(attribute.mappingType)) {
                configIndex = advIdx;
                advIdx++;
            } else if (attribute.mappingType === MappingType.ADVANCED.toString()) {
                configIndex = advcdIdx;
                advcdIdx++;
            } else {
                configIndex = basicIdx;
                basicIdx++;
            }
            if (!attribute.mappingType || attribute.mappingType === '') {
                violations.push({
                    index,
                    violationType: ViolationType.MappingMethod,
                    targetAttribute: attribute.name
                });
            }
            if (!!attribute.mappingType && isSourceColumnInvalid(attribute, mappingDirective.attributeMappingInfo, mappingDirective.targetEntityReference.name)) {
                violations.push({
                    index: configIndex,
                    violationType: ViolationType.SourceColumn,
                    targetAttribute: attribute.name,
                    mappingMethod: attribute.mappingType
                });
            }
            if (!attribute.name || attribute.name === '') {
                violations.push({
                    index,
                    violationType: ViolationType.TargetColumn,
                });
            }
        });
    }
    return violations;
}

export function isSourceColumnInvalid(attribute: AttributeMapping, sourceAttributes: AttributeMapping[], targetEntity: string): boolean {
    switch (attribute.mappingType) {
        case MappingType.DIRECT:
        case MappingType.DERIVED:
        case MappingType.AGGREGATE:
            if (!attribute.attributeReference || !attribute.attributeReference.name || attribute.attributeReference.name === '') {
                return true;
            }
            break;
        case MappingType.SURROGATEKEY:
            if (isSurrogateKeyColumnInvalid(attribute, sourceAttributes)) {
                return true;
            }
            break;
        case MappingType.LOOKUP:
            if (isLookupColumnInvalid(attribute, sourceAttributes, targetEntity)) {
                return true;
            }
            break;
        case MappingType.UNPIVOT:
            if (isUnpivotColumnInvalid(attribute, sourceAttributes)) {
                return true;
            }
            break;
        case MappingType.ADVANCED:
            if (!attribute.expression || !attribute.expression.expression || attribute.expression.expression === '') {
                return true;
            }
            break;
        default:
            return true;
    }

    return false;
}

export function isLookupColumnInvalid(attribute: AttributeMapping, sourceAttributes: AttributeMapping[], targetEntity: string): boolean {
    // validate lookup Source column is non-null
    if (!attribute.lookupFrom || !attribute.lookupFrom.attribute || attribute.lookupFrom.attribute === '') {
        return true;
    }

    // validate atleast one lookupJoinPair is added
    if (!attribute.lookupFrom.joinPairs || attribute.lookupFrom.joinPairs.length === 0) {
        return true;
    }

    for (const joinPair of attribute.lookupFrom.joinPairs) {
        // validate lookup joinPair values are non-null
        if (joinPair.fromAttribute.name === '' || joinPair.toAttribute === '' || joinPair.operator === '') {
            return true;
        }

        // validate lookup from attribute and joinPair toAttribute are not same.
        if (attribute.lookupFrom.attribute === joinPair.toAttribute) {
            return true;
        }
    }

    // validate lookupFrom entity and traget entity are not same.
    if (attribute.lookupFrom.entity === targetEntity) {
        return true;
    }

    return false;
}

export function isSurrogateKeyColumnInvalid(attribute: AttributeMapping, sourceAttributes: AttributeMapping[]): boolean {
    // validate atleast one alternate key is added.
    if (!attribute.alternateKeys || attribute.alternateKeys.length === 0) {
        return true;
    }
    for (const alternateKey of attribute.alternateKeys) {
        // validate each alternate key is mapped.
        if (sourceAttributes.findIndex((x) => x.name === alternateKey) === -1) {
            return true;
        }
        // validate alternate key and surrogate key are not same.
        if (attribute.name === alternateKey) {
            return true;
        }
    }

    return false;
}

export function isUnpivotColumnInvalid(attribute: AttributeMapping, sourceAttributes: AttributeMapping[]): boolean {
    // validate unpivot key is non-null
    if (!attribute.unpivotInfo || !attribute.unpivotInfo.targetUnpivotKeyAttribute || attribute.unpivotInfo.targetUnpivotKeyAttribute === '') {
        return true;
    }
    // validate atleast one unpivot key column is added.
    if (!attribute.unpivotInfo.sourceUnpivotAttributes ||
        attribute.unpivotInfo.sourceUnpivotAttributes.length === 0) {
        return true;
    }
    // validate non-null unpivot key column are added.
    for (const srcUnpivotAttr of attribute.unpivotInfo.sourceUnpivotAttributes) {
        if (srcUnpivotAttr.attributeReference.name === '') {
            return true;
        }
    }
    // validate unpivot target key and key column are not same.
    if (attribute.name === attribute.unpivotInfo.targetUnpivotKeyAttribute) {
        return true;
    }
    // validate some other transformation row is added for ungroupBy columns.
    if (sourceAttributes.length === 1) {
        return true;
    }

    return false;
}
